import React, { useMemo, useRef, useState } from "react";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ChatEntityPickerModal from "./ChatEntityPickerModal";

const MAX_FILE_BYTES = 50 * 1024 * 1024;

const getForwardTitle = (message) => {
    if (!message) return "";
    return message.text || message.fileName || message.entity?.title || "Пересланное сообщение";
};

const formatBytes = (value) => {
    const bytes = Number(value || 0);
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

export default function InputChatUI({
    text,
    setText,
    file,
    setFile,
    inputRef,
    officeId = null,
    forwardDraft = null,
    onCancelForward = null,
    onSend,
    onShareEntity = null,
    onVoiceFinished,
    sending = false,
    sendingProgress = 0,
    sendError = null,
}) {
    const imageInputRef = useRef(null);
    const docInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const startedAtRef = useRef(0);
    const cancelledRef = useRef(false);

    const [attachOpen, setAttachOpen] = useState(false);
    const [entityPickerOpen, setEntityPickerOpen] = useState(false);
    const [recording, setRecording] = useState(false);
    const [recordTime, setRecordTime] = useState(0);
    const [localError, setLocalError] = useState("");

    const canSend = !!(text.trim() || file || forwardDraft) && !sending;
    const visibleError = localError || sendError || "";
    const progress = Math.min(100, Math.max(0, Number(sendingProgress || 0)));

    const fileMeta = useMemo(() => {
        if (!file) return "";
        const parts = [formatBytes(file.size), file.type || "файл"].filter(Boolean);
        return parts.join(" · ");
    }, [file]);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!canSend) return;
        setLocalError("");
        onSend?.({ text, file });
    };

    const handleKeyDown = (event) => {
        if (event.key !== "Enter" || event.shiftKey || event.nativeEvent?.isComposing) return;
        event.preventDefault();
        if (canSend) onSend?.({ text, file });
    };

    const handleFile = (event) => {
        const selected = event.target.files?.[0] || null;
        event.target.value = "";
        setAttachOpen(false);

        if (!selected) return;

        if (selected.size > MAX_FILE_BYTES) {
            setLocalError("Файл слишком большой. Максимум 50 МБ.");
            return;
        }

        setLocalError("");
        setFile(selected);
    };

    const clearRecordTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const stopStream = () => {
        if (!streamRef.current) return;
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
    };

    const startRecord = async () => {
        if (recording || sending) return;

        if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
            setLocalError("Этот браузер не поддерживает запись голосовых сообщений.");
            return;
        }

        try {
            setLocalError("");
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            streamRef.current = stream;
            chunksRef.current = [];
            cancelledRef.current = false;
            startedAtRef.current = Date.now();

            const recorder = new MediaRecorder(stream);
            mediaRecorderRef.current = recorder;

            recorder.ondataavailable = (event) => {
                if (event.data?.size) chunksRef.current.push(event.data);
            };

            recorder.onstop = () => {
                clearRecordTimer();
                stopStream();

                const durationMs = Date.now() - startedAtRef.current;
                const mimeType = recorder.mimeType || "audio/webm";
                const blob = new Blob(chunksRef.current, { type: mimeType });

                setRecording(false);
                setRecordTime(0);
                chunksRef.current = [];
                mediaRecorderRef.current = null;

                if (!cancelledRef.current && blob.size > 0) {
                    onVoiceFinished?.({ blob, durationMs });
                }
            };

            recorder.start();
            setRecording(true);
            setRecordTime(0);

            timerRef.current = setInterval(() => {
                setRecordTime(Math.floor((Date.now() - startedAtRef.current) / 1000));
            }, 300);
        } catch (_error) {
            setRecording(false);
            clearRecordTimer();
            stopStream();
            setLocalError("Нет доступа к микрофону. Проверьте разрешение браузера.");
        }
    };

    const stopRecord = () => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") return;
        cancelledRef.current = false;
        mediaRecorderRef.current.stop();
    };

    const cancelRecord = () => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") return;
        cancelledRef.current = true;
        mediaRecorderRef.current.stop();
    };

    const formatRecordTime = () => {
        const min = Math.floor(recordTime / 60);
        const sec = recordTime % 60;
        return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    };

    if (recording) {
        return (
            <div className="chat-input-AO chat-input-AO--recording">
                <button type="button" className="chat-input-AO__cancel" onClick={cancelRecord}>Отмена</button>
                <div className="chat-input-AO__recording-state">
                    <span className="chat-input-AO__pulse" />
                    <strong>Запись</strong>
                    <span>{formatRecordTime()}</span>
                </div>
                <button type="button" className="chat-input-AO__send" onClick={stopRecord} title="Отправить голосовое">➤</button>
            </div>
        );
    }

    return (
        <form className="chat-input-AO" onSubmit={handleSubmit}>
            <div className="chat-input-AO__composer">
                {forwardDraft && (
                    <div className="chat-input-AO__forward-draft">
                        <div>
                            <b>Пересылка</b>
                            <span>{getForwardTitle(forwardDraft)}</span>
                        </div>
                        <button type="button" onClick={onCancelForward} aria-label="Отменить пересылку">✕</button>
                    </div>
                )}

                {file && (
                    <div className="chat-input-AO__file">
                        <span className="chat-input-AO__file-icon">📎</span>
                        <div>
                            <b>{file.name}</b>
                            {fileMeta && <small>{fileMeta}</small>}
                        </div>
                        <button type="button" onClick={() => setFile(null)} disabled={sending} aria-label="Убрать файл">✕</button>
                    </div>
                )}

                <div className="chat-input-AO__row">
                    <button
                        type="button"
                        className="chat-input-AO__icon"
                        onClick={() => setAttachOpen((state) => !state)}
                        title="Прикрепить"
                        disabled={sending}
                    >
                        <AttachFileIcon sx={{ fontSize: 20 }} />
                    </button>

                    <textarea
                        ref={inputRef}
                        value={text}
                        rows={1}
                        placeholder={forwardDraft ? "Добавьте комментарий…" : "Сообщение…"}
                        onChange={(event) => setText(event.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={sending}
                        aria-label="Сообщение"
                    />

                    <button
                        type="button"
                        className="chat-input-AO__icon chat-input-AO__icon--voice"
                        onClick={startRecord}
                        title="Голосовое сообщение"
                        disabled={sending}
                    >
                        🎙
                    </button>

                    <button
                        type="submit"
                        className="chat-input-AO__send"
                        disabled={!canSend}
                        title="Отправить"
                    >
                        {sending ? <span className="chat-input-AO__spinner" /> : "➤"}
                    </button>
                </div>

                {sending && file && (
                    <div className="chat-input-AO__progress" aria-label={`Загрузка ${progress}%`}>
                        <span style={{ width: `${Math.max(4, progress)}%` }} />
                        <small>{progress > 0 ? `Отправляем файл · ${progress}%` : "Подготавливаем файл…"}</small>
                    </div>
                )}

                {visibleError && <div className="chat-input-AO__error" role="alert">{visibleError}</div>}
            </div>

            <input ref={imageInputRef} type="file" accept="image/*,video/*" hidden onChange={handleFile} />
            <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.7z" hidden onChange={handleFile} />

            {attachOpen && (
                <>
                    <button type="button" className="chat-input-AO__backdrop" onClick={() => setAttachOpen(false)} aria-label="Закрыть меню вложений" />
                    <div className="chat-input-AO__attach">
                        <button type="button" onClick={() => imageInputRef.current?.click()}>
                            <span>▧</span>
                            <div><b>Фото или видео</b><small>JPG, PNG, WEBP, MP4 и другие</small></div>
                        </button>
                        <button type="button" onClick={() => docInputRef.current?.click()}>
                            <span>▤</span>
                            <div><b>Документ</b><small>PDF, Office, архивы до 50 МБ</small></div>
                        </button>

                        {officeId && onShareEntity ? (
                            <button
                                type="button"
                                onClick={() => {
                                    setAttachOpen(false);
                                    setEntityPickerOpen(true);
                                }}
                            >
                                <span>◆</span>
                                <div>
                                    <b>Объект офиса</b>
                                    <small>Заказ, товар, задание, сотрудник или 3D-сессия</small>
                                </div>
                            </button>
                        ) : null}
                    </div>
                </>
            )}

            <ChatEntityPickerModal
                open={entityPickerOpen}
                officeId={officeId}
                sending={sending}
                onClose={() => setEntityPickerOpen(false)}
                onSelect={onShareEntity}
            />
        </form>
    );
}
